import { Spine } from "@esotericsoftware/spine-pixi-v7";
import anime from "animejs";
import * as PIXI from "pixi.js";
import {
  setShowFreeSpinTotalWin,
  setShowTransitionDialog,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import {
  startOutroWinMusic,
  stopOutroWinMusic,
} from "../../../../Games/Shared/outroWinMusic";
import TickerTimeout from "../../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle, WIN_TEXT_SCALE } from "../../../../fontStyles";
import { formatToCustomLocale } from "../../../../utilities";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import attachTapToContinue from "../../../../utils/attachTapToContinue";
import store from "../../../../State/Store/store";

const SPINE_NAME = "win_celebration";
const SKIN_NAME = "total_win";
// Hold & Win's own total win (animator, 2026-09-30): `hold_and_win_outro`,
// default skin only, in / idle / out, the same txt_slot_value /
// txt_slot_xbet_value slots. Its tap bone is a hand edit (0, -480.24) — this
// spine's Total Win position, so the table below lands it on the shared line.
// JSON fixes (the export named them wrong): the clips `in` and `ou` were
// SWAPPED — `in` collapsed the art, `ou` built it — so they are renamed
// `out` and `in`; the tap slot/bone `txt_slot_taptostart` is
// `txt_slot_tap_to_continue`, as on every total win spine.
export const HOLD_AND_WIN_TOTAL_WIN_SPINE = "hold_and_win_outro";

// ============================================================================
// Sizing — the WinAnimationSecondary baseline, as every Mad Labs popup takes
// it. All 1.0 until a device proves otherwise.
// ============================================================================
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the word at the SAME screen position, so opening two at
// once shows them perfectly aligned. Total win sets that position: its bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has.
//
// This spine's tap bone is at -480.24, so it needs -43.66 to reach the same line.
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// Natural size runs too large on a standard landscape phone.
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_DESKTOP = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)

const VALUE_SLOTS = ["txt_slot_value"];
// The current win_celebration export calls this `txt_slot_xbet_value`; Zeus
// called it `txt_slot_multiplier`. Tried in order — the three Pot Collection
// popups still ask only for the Zeus name and so draw no multiplier at all.
const MULTIPLIER_SLOTS = ["txt_slot_xbet_value", "txt_slot_multiplier"];
const BUTTON_SLOTS = ["txt_slot_tap_to_continue", "txt_slot_taptocontinue"];

// Unlike Max Win, Total Win DOES auto-close — a player who walks away is not
// left staring at it. Borrowed from the Pot Collection outro's own constant
// until free spins has a config block of its own.
const AUTO_CLOSE_DELAY =
  FEATURES_CONFIG.POT_COLLECTION.TOTAL_WIN_AUTO_CLOSE || 30000;

// How long the counter takes to run up to the win.
const COUNTER_DURATION_MS = 2000;

/**
 * ── There is NO skip protection any more, deliberately ──
 *
 * There was: input was locked for the length of the `in` clip, 1.000s, while
 * the counter — which starts in the same tick and runs 2.000s — climbed
 * unskippably through its first half. Vladimir, 2026-09-10: "as soon as the
 * counter starts, the user should be able to skip it."
 *
 * It was protecting the xBet line and the tap word, which `in` KEYS: fading
 * them in over that clip means the spine fights them every frame and again when
 * it hands to `idle`. But that is not what the lock was needed for any more —
 * `_revealAfterCounter` already refuses to reveal onto `in` and replays itself
 * off the idle instead (see `_revealPending`, and the inIdleTimeout in
 * startSequence). The latch is the real guard; the lock was its blunt ancestor.
 *
 * So a tap at 0.4s now lands the number instantly, `in` plays out, and the two
 * texts fade in when the idle takes the track. CLICK_DEBOUNCE_MS still stops a
 * double tap from jumping the counter and closing in one gesture.
 *
 * NOT removed from the Zero Win, Max Win or Additional Free Spins dialogs: none
 * of them has a counter or a post-counter reveal, so their timer is doing a
 * different job — refusing to be dismissed during its own intro — and dropping
 * it there would be a separate decision.
 */
const CLICK_DEBOUNCE_MS = 300;

/**
 * FreeSpinTotalWinController — everything won across the free-spin feature.
 *
 * The `total_win` skin of the shared `win_celebration` spine, run the way
 * PotCollectionOutroDialogController runs its own. Four things separate it
 * from the Max Win popup next door:
 *
 *   1. The counter RUNS UP to the value; Max Win simply shows it.
 *   2. It AUTO-CLOSES, so an abandoned session does not sit on it for ever.
 *   3. A tap during the count-up JUMPS to the total instead of closing —
 *      the first tap finishes the counter, the second dismisses.
 *   4. The close flag is dispatched SYNCHRONOUSLY. See finishAnimation.
 *
 * RELOAD IS NOT HANDLED HERE, and deliberately so. Pot Collection survives a
 * refresh on its total win because RGS synthesises `phase: "completion"` out
 * of localStorage and replays the end of the bonus, which re-dispatches this
 * popup. That machinery is all pot-grid shaped — saveBonusGameRound, the
 * collection replay — and none of it transfers. The free-spin equivalent
 * belongs with the free-spins slice, and lands with it.
 */
class FreeSpinTotalWinController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
    // The flag this popup lowers on close. The free-spins one by default;
    // Hold & Win reuses this popup with its own (setShowHoldAndWinTotalWin).
    closeAction = setShowFreeSpinTotalWin,
    // The spine to draw — win_celebration's `total_win` skin by default,
    // Hold & Win passes HOLD_AND_WIN_TOTAL_WIN_SPINE (no skin to set).
    spineName = SPINE_NAME,
  ) {
    this.closeAction = closeAction;
    // Hold & Win's popup (its own close flag) — what decides the exit wipe
    // now that both variants may draw the same spine.
    this.isHoldAndWin = closeAction !== setShowFreeSpinTotalWin;
    this.spineName = spineName;
    this.skinName = spineName === SPINE_NAME ? SKIN_NAME : null;
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext?.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;
    this.debounceTimeout = null;
    this.inIdleTimeout = null;
    this._showRAF = null;

    this.counterText = null;
    this.multiplierText = null;
    this.buttonText = null;
    this._fitTapButton = null;

    this._totalWinValue = 0;
    this._betAmount = 0;
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;
    this.autoCloseTimeout = null;
    this._counterFinished = false;
    // Held when the counter lands while `in` still owns the track.
    this._revealPending = false;
    // "The out clip has started" — see playClosingAnimation.
    this._closing = false;
    // Latch for _revealAfterCounter, so the xBet line and the tap word are
    // revealed exactly once per showing.
    this._revealedAfterCounter = false;
    this._wasAmbientActive = false;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeSpine();
  }

  // ── Sizing ───────────────────────────────────────────────────────────────

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.spine.scale.set(scale);
    this.spine.x = 0;
    this.spine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

    this._fitTapButton?.(this.dimensions);
    this.applyButtonOffset();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDims = this.dimensionsContext?.dimensions;
    const newDims = dimensionsContext?.dimensions;

    const changed =
      !oldDims ||
      oldDims.width !== newDims?.width ||
      oldDims.height !== newDims?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;
    if (!changed) return;

    const deviceChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;

    if (deviceChanged && this.buttonText) this.updateButtonText();
    this.applyScaleAndCenter();
    // Both texts refit against the new screen width. The counter refits at
    // its CURRENT value, not the total — rotating mid-count-up would otherwise
    // flash the final number and then rewind.
    this.updateCounterDisplay(this.currentCounterValue ?? this._totalWinValue);
    this.updateMultiplierDisplay(this._totalWinValue);
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(`${this.spineName}-skeleton`) ||
        !assets.has(`${this.spineName}-atlas`)
      ) {
        console.warn("[FreeSpinTotalWin] Spine not loaded:", this.spineName);
        return;
      }

      this.spine = Spine.from({
        skeleton: `${this.spineName}-skeleton`,
        atlas: `${this.spineName}-atlas`,
      });
      if (!this.spine?.skeleton || !this.spine?.state) return;

      this.container.addChild(this.spine);
      // Hidden by ALPHA, never by unmounting — that is what gives `out` frames.
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      this.spine.zIndex = 20;

      this.applySkin();
      this.applyScaleAndCenter();
      this.initialized = true;

      this.warmUpSpine();
      this.initializeCounter();
      this.initializeMultiplier();
      this.initializeButton();
    } catch (error) {
      console.warn("[FreeSpinTotalWin] Init failed:", error);
    }
  }

  applySkin() {
    // Hold & Win's spine is one picture (default skin) — nothing to set.
    if (!this.skinName) return;
    const data = this.spine?.skeleton?.data;
    if (!data?.findSkin(this.skinName)) {
      console.warn("[FreeSpinTotalWin] Missing skin:", this.skinName);
      return;
    }
    this.spine.skeleton.setSkinByName(this.skinName);
    // Slots only — setToSetupPose would reset BONES and snap the pose.
    this.spine.skeleton.setSlotsToSetupPose();
  }

  warmUpSpine() {
    if (!this.spine?.state) return;
    try {
      if (this.spine) this.spine.autoUpdate = true;
      this.spine.state.setAnimation(0, "in", false);
      this.spine.state.update(0.016);
      // Spine 4.2 REQUIRES an argument here.
      if (this.spine.skeleton.physics !== undefined) {
        this.spine.skeleton.updateWorldTransform(2);
      } else {
        this.spine.skeleton.updateWorldTransform(0);
      }
      this.spine.state.clearTracks();
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      SpineAtlasUploader.upload(this.spineName);
    } catch {
      // Optimisation only.
    }
  }

  _resolveSlot(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /** Largest font size at which `text` still fits the screen. Never scales up. */
  _fitFontSizeToWidth(text, baseStyle, baseFontSize, screenRatio = 0.85) {
    const spineScale = this.spine?.scale?.x || 1;
    const maxWidth = ((this.dimensions?.width || 800) * screenRatio) / spineScale;
    const metrics = PIXI.TextMetrics.measureText(
      text,
      new PIXI.TextStyle(scaleStyle(baseStyle, baseFontSize)),
    );
    if (metrics.width <= maxWidth || maxWidth <= 0) return baseFontSize;
    return Math.max(1, Math.round(baseFontSize * (maxWidth / metrics.width)));
  }

  _addSlotText(slotName, initialText, fontSize) {
    if (!slotName) return null;
    const container = new PIXI.Container();
    container.name = `free_spin_total_win_${slotName}`;
    const text = new PIXI.Text(
      initialText,
      new PIXI.TextStyle(scaleStyle(this._counterBaseStyle, fontSize)),
    );
    text.resolution = 2;
    text.anchor.set(0.5);
    container.addChild(text);
    this.spine.addSlotObject(slotName, container);
    return text;
  }

  initializeCounter() {
    this._counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    // WIN_TEXT_SCALE, so this popup and the base game's win celebration draw
    // the same spine at the same size — see fontStyles.
    this._counterBaseFontSize = Math.round(
      this._counterBaseStyle.fontSize * WIN_TEXT_SCALE,
    );
    this.counterText = this._addSlotText(
      this._resolveSlot(VALUE_SLOTS),
      "0",
      this._counterBaseFontSize,
    );
  }

  initializeMultiplier() {
    // ── From the xBet style's own size, not 0.6 of the counter ──
    //
    // 0.6 was inherited from the Pot Collection popup. It happened to land
    // within a pixel of the right answer, and it broke the moment the counter
    // moved: a fraction OF the counter follows every change to it, so raising
    // one raised the other and the two lines kept their ratio instead of their
    // sizes. The base game's win popup takes 48 from `winCelebrationXBet`, so
    // this does too, and both then scale by the same factor.
    this._multiplierBaseFontSize = Math.round(
      FONT_STYLES.winCelebrationXBet.fontSize * WIN_TEXT_SCALE,
    );
    const slotName = this._resolveSlot(MULTIPLIER_SLOTS);
    if (!slotName) {
      console.warn("[FreeSpinTotalWin] No multiplier slot:", MULTIPLIER_SLOTS);
      return;
    }
    // A rendered placeholder, not "". The slot is keyed by `in` alongside the
    // value, so an empty string means the line animates in as nothing at all —
    // which reads as a missing element rather than a pending one.
    this.multiplierText = this._addSlotText(
      slotName,
      this._multiplierString(0),
      this._multiplierBaseFontSize,
    );
    // Hidden until the counter lands — see _revealAfterCounter.
    //
    // Safe to set alpha here: _addSlotText hands back the INNER text, and only
    // the wrapping container is given to addSlotObject. The runtime rewrites
    // that container every frame and never touches its children — the same
    // reason attachTapToContinue scales its text rather than its container.
    if (this.multiplierText) this.multiplierText.alpha = 0;
  }

  initializeButton() {
    const built = attachTapToContinue({
      spine: this.spine,
      slotNames: BUTTON_SLOTS,
      label: this._buttonLabel(),
      name: "free_spin_total_win_continue_attached_to_slot",
      // ── The TAP makes no sound of its own ──
      //
      // It used to play win_animation_end whenever handleUserClick() returned
      // true — and that returns true on BOTH of its branches: skipping the
      // counter and closing the popup. So the first tap (skip) sounded the
      // close, and the second tap sounded it again.
      //
      // The boolean means "the tap was accepted", not "the popup closed". The
      // sound now lives in playClosingAnimation, which is the only thing that
      // knows the out clip is running. Vladimir, 2026-09-18: "when I stop the
      // counter I don't need that sound, I need it only when we play out
      // animation".
      onClick: () => {
        this.handleUserClick();
      },
    });
    if (!built) return;
    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._setTapOffsetY = built.setOffsetY;
    this._revealMs = built.revealMs;
    this._fitTapButton(this.dimensions);
    this.applyButtonOffset();
    // Hidden until the counter lands, same as the multiplier. The spine keys
    // this slot during `in`, so without this the word arrives while the number
    // is still climbing and invites a tap at the wrong moment.
    if (this.buttonText) this.buttonText.alpha = 0;
  }

  /**
   * Reveal the xBet line and the tap word — once the counter has LANDED.
   *
   * Pot Collection's total win does exactly this: the number counts up alone,
   * and only when it settles do the multiplier and the prompt fade in together.
   * Both slots are keyed by the spine's `in`, so they would otherwise appear
   * while the count is still running — the multiplier showing a figure that is
   * not final, and the prompt asking for a tap mid-count.
   *
   * Called from BOTH ways the counter can end: its own completion, and the
   * first tap jumping it to the total. Latched, so whichever arrives second
   * finds nothing to do.
   */
  _revealAfterCounter() {
    if (this._revealedAfterCounter) return;
    // A tap that jumps the counter and a tap that closes can be the same
    // gesture, twice. Fading text in over `out` is the second half of the flash.
    if (this._closing || !this.isPlaying) return;

    // ── Never onto `in` ──
    //
    // `in` KEYS both of these slots, so anything faded in over it is fought by
    // the spine's own keys every frame and again when the clip hands over to
    // `idle` — the flash and re-render a skip produced. The count-up normally
    // outlasts `in` so this never came up; a tap that jumps the counter can
    // land anywhere in it.
    //
    // So the reveal is a REQUEST while the intro owns the track, replayed the
    // moment the idle takes it — see the inIdleTimeout in show(). Same rule as
    // WinAnimationSecondaryController's xBet gate, and for the same reason.
    if (this.spine?.state?.tracks?.[0]?.animation?.name !== "idle") {
      this._revealPending = true;
      return;
    }

    this._revealPending = false;
    this._revealedAfterCounter = true;

    // 400ms easeOutCubic — the same fade Pot Collection's outro uses.
    for (const text of [this.multiplierText, this.buttonText]) {
      if (!text) continue;
      // Idempotent: one fade per text, ever. Two overlapping tweens on the same
      // alpha read as a stutter rather than a fade.
      anime.remove(text);
      text.alpha = 0;
      anime({
        targets: text,
        alpha: 1,
        duration: 400,
        easing: "easeOutCubic",
      });
    }
  }

  /**
   * Lift the tap word off its bone, in SPINE UNITS.
   *
   * On the TEXT, because the spine rewrites the container's transform every
   * frame. Spine units rather than a multiple of symbolHeight: text.y is
   * spine-local, so the spine's own scale carries it, and the value can be
   * read straight off the bone geometry. The old form reduced to
   * `offset x SYMBOL_HEIGHT / deviceScale` anyway — the same constant, just
   * two indirections away from the numbers it had to match.
   */
  applyButtonOffset() {
    if (!this._setTapOffsetY) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this._setTapOffsetY(buttonYOffset);
  }

  _buttonLabel() {
    // TAP on every device — see FreeSpinOutroDialogController for why.
    return this.t("tap_to_continue").toUpperCase();
  }

  updateButtonText() {
    if (!this.buttonText) return;
    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  // ── Values ───────────────────────────────────────────────────────────────

  /**
   * The win and the bet it was made at.
   *
   * The counter is left at ZERO on purpose — startSequence runs it up. Max Win
   * paints the final number here because it has no count-up; doing the same
   * would show the total before the animation had a chance to reach it.
   */
  setTotalWinValue(totalWin, bet) {
    this._totalWinValue = totalWin || 0;
    this._betAmount = bet || 0;
    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    this.updateMultiplierDisplay(this._totalWinValue);
  }

  /**
   * Start the counter loop, if it is not already running.
   *
   * Identical to WinAnimationSecondaryController.startCounterSound — the win
   * celebration and the total win must sound the same. `counter` is a LOOPING
   * Howl, and Howler answers play() on a running Howl with a SECOND sound id
   * rather than a restart, so two would stack and the tick would double.
   */
  startCounterSound() {
    if (this.counterSoundPlaying) return;
    this.counterSoundPlaying = true;
    soundManager.play(SOUND_NAME.counter);
  }

  /**
   * Stop the counter loop.
   *
   * `playEnd` marks the moment the numbers land, using the SAME `counter`
   * sound played ONCE instead of looping — there is no separate end asset.
   *
   * Deliberately NOT guarded on `counterSoundPlaying`: this Howl is shared
   * with the other win screen, so whichever controller stops it must always
   * reach soundManager. Guarding the stop is what left it looping for ever.
   */
  stopCounterSound(playEnd = false) {
    this.counterSoundPlaying = false;
    soundManager.stop(SOUND_NAME.counter);
    if (playEnd) soundManager.playOnce(SOUND_NAME.counter);
  }

  /**
   * Run the counter from `fromValue` to `toValue`.
   * Same shape as WinAnimationSecondaryController and the PC outro.
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    this.counterAnimationRef?.pause();
    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);
    this.startCounterSound();

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration,
      easing: "easeOutQuad",
      update: () => this.updateCounterDisplay(this.currentCounterValue),
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;
        this.stopCounterSound(true);
        this._counterFinished = true;
        this._revealAfterCounter();
        onComplete?.();
      },
    });
  }

  /** Skip the run-up and land on the total. What the FIRST tap does. */
  jumpCounterToValue(value) {
    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    this.stopCounterSound(true);
    this._counterFinished = true;
    // A SKIP SKIPS THE CLIP TOO — see below. Without this the reveal is only
    // requested here and lands whenever `in` happens to end.
    this._cutIntroToIdle();
    this._revealAfterCounter();
  }

  /**
   * End `in` now and hand the track to `idle`.
   *
   * ── Why a skip has to move the SPINE, not just the counter ──
   *
   * `in` KEYS the xBet and tap-word slots, so nothing can be faded onto it —
   * `_revealAfterCounter` refuses and parks a request, which `inIdleTimeout`
   * releases when `in` ends. That is correct for a counter that outlasts `in`,
   * and wrong for a skip: the tap stopped the NUMBER but left the CLIP running,
   * so the xBet and the tap word arrived whenever `in` happened to finish —
   * anywhere up to its full length after the press, depending only on where the
   * player tapped. Vladimir, on the demo build: the popup could be closed
   * before either of them was ever on screen.
   *
   * A skip means "show me the end of this", so it ends the clip. The idle is
   * the state those slots are open in, so going there is what makes the reveal
   * possible AT the press instead of some time after it.
   *
   * The timer goes with it. It is armed for the whole of `in` and would
   * otherwise fire later and set a SECOND looping idle — the double render a
   * fast skip used to produce, recorded on the timer itself.
   */
  _cutIntroToIdle() {
    if (!this.isPlaying || this._closing || !this.spine?.state) return;
    if (this.spine.state.tracks?.[0]?.animation?.name !== "in") return;

    this.inIdleTimeout?.clear();
    this.inIdleTimeout = null;
    this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "idle", true);
  }

  updateCounterDisplay(value) {
    if (!this.counterText) return;
    const text = formatToCustomLocale(Math.floor(value));
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._counterBaseFontSize,
    );
    this.counterText.text = text;
    this.counterText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
  }

  /** "12.5x BET" — one formatter, so the placeholder cannot drift from the real value. */
  _multiplierString(multiplier) {
    const bet = this.t("bet").toUpperCase();
    return `${multiplier.toLocaleString("en-US")}x ${bet}`;
  }

  updateMultiplierDisplay(winAmount) {
    if (!this.multiplierText) return;

    // Falls back to the placeholder rather than bailing out. Bailing left the
    // last string on screen — and at startup that was the empty one, so the
    // line simply never appeared.
    const multiplier =
      this._betAmount > 0
        ? parseFloat((winAmount / this._betAmount).toFixed(2))
        : 0;

    const text = this._multiplierString(multiplier);
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._multiplierBaseFontSize,
    );
    this.multiplierText.text = text;
    this.multiplierText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
  }

  getSpineAnimationDuration(name) {
    const anim = this.spine?.skeleton?.data?.animations?.find(
      (a) => a.name === name,
    );
    return anim?.duration ? Math.round(anim.duration * 1000) : 1000;
  }

  _clearTimeouts() {
    for (const key of [
      "debounceTimeout",
      "inIdleTimeout",
      "autoCloseTimeout",
    ]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  startSequence() {
    if (this.isPlaying || !this.spine?.state) return;

    this.isPlaying = true;
    this.isWaitingForClick = false;
    this._clearTimeouts();

    this.overlayRef?.current?.fadeInBackground();

    // ── The feature track KEEPS PLAYING under this popup ──
    //
    // It used to be ducked here and faded back in on close, which is Pot
    // Collection's shape. It is wrong for this screen: the total win is the
    // last beat of the free spins, not an interruption of them, and cutting
    // the room's music to announce what the room paid left a hole and then
    // put the same track back a moment later for no reason.
    //
    // The music is handed over exactly ONCE, and the TRANSITION owns it: the
    // wipe out of the room fades the feature track down as it starts and
    // fades the base track in when it completes. So the player hears one
    // continuous piece of music from the last free spin to the base game, with
    // the announcement over the top of it.
    //
    // `_wasAmbientActive` is no longer read on close; nothing was ducked.
    // There is no `total_win_loop` in this game and there never was — the
    // announcement is a ONE-SHOT over the music that is already playing.
    // Removed 2026-09-23 with Vladimir: *"we dont have that total win loop
    // music for this game so we will think of something different."*
    soundManager.play(SOUND_NAME.total_win);
    // ...and the celebration's loop underneath it, until this popup closes.
    // Both outro popups do exactly this — see outroWinMusic.
    startOutroWinMusic();

    this.applyScaleAndCenter();

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.state.apply(this.spine.skeleton);
    this.spine.skeleton.setToSetupPose();
    // setToSetupPose reverts the skin's slot state, so re-apply after it.
    this.applySkin();

    // Played while STILL HIDDEN, shown on the NEXT frame.
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "in", false);
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) this.spine.alpha = 1;
    });

    const inDuration = this.getSpineAnimationDuration("in");
    this.inIdleTimeout = new TickerTimeout(() => {
      // ── Not over the CLOSE ──
      //
      // Two taps in quick succession finish the counter and then dismiss, and
      // both can land inside `in`, which outlasts them. This timer was armed for
      // the whole of `in`, so it fired afterwards and set a LOOPING idle on top
      // of `out` — the popup drew itself again, held, and its close listener
      // never saw the clip it was waiting for. That is the double render a fast
      // skip produced.
      if (!this.isPlaying || this._closing) return;
      if (this.spine) this.spine.autoUpdate = true;
      this.spine.state.setAnimation(0, "idle", true);
      // The idle owns the slots now — release a reveal the intro held back.
      if (this._revealPending) this._revealAfterCounter();
    }, inDuration);

    // The counter starts with the `in` animation and runs alongside it, so the
    // number is already climbing as the art arrives.
    this._counterFinished = false;
    // Re-armed per showing, or a second total win would open with both texts
    // already up.
    this._revealedAfterCounter = false;
    this._revealPending = false;
    this._closing = false;

    // ── And HIDDEN again, which the latches above do not do ──
    //
    // The reveal leaves both texts at alpha 1. Re-arming the latches only means
    // the reveal will run AGAIN — it says nothing about what the texts are
    // currently showing, so every total win after the first in a session opened
    // with the xBet line and the tap word already up, over a counter still
    // climbing. One bonus a session hid it completely.
    //
    // `anime.remove` first: a fade still in flight from the previous showing
    // holds its own reference to the text and would animate it straight back to
    // 1, one frame after this set it to 0.
    for (const text of [this.multiplierText, this.buttonText]) {
      if (!text) continue;
      anime.remove(text);
      text.alpha = 0;
    }
    this.startCounterAnimation(
      0,
      this._totalWinValue,
      COUNTER_DURATION_MS,
      () => this._startAutoClose(),
    );
  }

  /**
   * TWO STAGES. The first tap finishes the counter, the second closes.
   *
   * Dismissing mid-count would rob the player of the number they are watching
   * arrive, so a tap during the run-up jumps it to the total instead.
   */
  handleUserClick() {
    if (!this.isPlaying || !this.spine?.state) return false;
    if (this.debounceTimeout) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, CLICK_DEBOUNCE_MS);

    if (!this._counterFinished) {
      this.jumpCounterToValue(this._totalWinValue);
      this._startAutoClose();
      return true;
    }

    if (!this.isWaitingForClick) return false;

    // ── NOTHING CLOSES BEFORE IT HAS BEEN SEEN ──
    //
    // `_cutIntroToIdle` makes the reveal land on the skip itself, so this is
    // normally already true by the time a second tap arrives. It is the net for
    // the case it cannot cover: the reveal is a 400ms fade, and it can only
    // start once the runtime is actually drawing those slots. Until it has
    // started, a close would take the xBet line and the tap word off screen
    // without the player ever seeing them — which is the bug as reported.
    //
    // A refused tap is not lost: the auto-close armed by the skip still ends
    // the popup on its own, so this can never strand the round.
    if (!this._revealedAfterCounter) return false;

    this.playClosingAnimation();
    return true;
  }

  /** Auto-close, armed once the total is on screen — never before. */
  _startAutoClose() {
    this.isWaitingForClick = true;
    this.autoCloseTimeout?.clear();
    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying) this.playClosingAnimation();
    }, AUTO_CLOSE_DELAY);
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.spine?.state) return;
    this.isWaitingForClick = false;
    // Said once, read by everything the intro left armed — see the inIdleTimeout
    // in show() and the guard in _revealAfterCounter. Cleared per showing.
    this._closing = true;
    // And the timer goes now rather than on its own schedule: it is the only
    // thing that can put a clip on this track behind our backs.
    this.inIdleTimeout?.clear();
    this.inIdleTimeout = null;

    // ── This screen closes with the CELEBRATION's sound ──
    //
    // `win_animation_end`, not `tap_to_continue`. The total win popup is the
    // end of the free-spins celebration, so it closes the way the win
    // animation closes rather than clicking like a UI button. Every other
    // tap-to-continue screen keeps tap_to_continue.
    //
    // HERE and not on the button: this method is the out clip, it is guarded
    // by `isWaitingForClick` so it runs exactly once, and it is reached by the
    // auto-close as well as by the tap — so the sound follows the animation
    // rather than the gesture.
    soundManager.play(SOUND_NAME.win_animation_end);
    // The loop leaves with the art; the wipe brings the base track in.
    stopOutroWinMusic();

    this.spine.state.clearListeners();
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "out", false);
    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Nothing to restore — the feature track was never ducked. Fading it
          // in here would raise a track that is already at full, and then the
          // wipe would fade it out again a beat later: two fades fighting over
          // one handover. The transition is the only place that swaps them.
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Close.
   *
   * The flag is dispatched SYNCHRONOUSLY, and that is not a style choice.
   * Every other popup clears it inside the overlay-fade callback, which is
   * safe because nothing is still asking for them. This one is dispatched at
   * the END of the feature, so the condition that opened it is still true for
   * as long as the flag is set — clearing it a fade later leaves a window in
   * which the trigger can fire again and the popup reopens. The Pot Collection
   * outro carries the same ordering, under a comment reading "CRITICAL: Close
   * the dialog FIRST (synchronous) to prevent re-trigger."
   *
   * The overlay fade is purely visual by then; the dialog is already closed.
   *
   * What FOLLOWS is the scene wipe back to the base game, started here on the
   * same handover every other popup in this chain uses — dispatch on the
   * spine's `out` complete, then fade. The wipe's own midpoint tears the round
   * down under full cover; nothing about leaving is decided here either.
   */
  finishAnimation() {
    if (!this.spine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;

    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    // Teardown, not a landing — no end stab.
    this.stopCounterSound(false);

    this.spine.alpha = 0;
    if (this.spine) this.spine.autoUpdate = false;
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    // FIRST, and synchronous. See above.
    this.dispatch?.(this.closeAction(false));
    // Then the way out. The wipe covers the screen and resets the round at its
    // midpoint, so the player never sees the rooms change back.
    // this.dispatch?.(setShowTransitionDialog(true));
    // Hold & Win's only when its room is really up — the dev panel's preview
    // (no round) just closes instead of wiping out of a room that is not there.
    // const isHoldAndWin = this.spineName === HOLD_AND_WIN_TOTAL_WIN_SPINE;
    // if (!this.isHoldAndWin || store.getState().holdAndWin?.active) {
    const state = store.getState();
    const devPreview = this.isHoldAndWin
      ? !state.holdAndWin?.active
      : FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && !state.freeSpins?.active;
    if (!devPreview) {
      this.dispatch?.(setShowTransitionDialog(true));
    }
    this.overlayRef?.current?.fadeOutBackground();
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.stopCounterSound(false);

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.spine.removeSlotObjects) this.spine.removeSlotObjects();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    this.counterText = null;
    this.multiplierText = null;
    this.buttonContainer = null;
    this.buttonText = null;
    this._fitTapButton = null;
    this.initialized = false;
  }
}

export default FreeSpinTotalWinController;
